import React from 'react';
import { Zap } from 'lucide-react';
import { useBoostTokens } from '@/hooks/useBoostTokens';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface BoostTokenDisplayProps {
  profileId?: string;
  className?: string;
}

const BoostTokenDisplay: React.FC<BoostTokenDisplayProps> = ({ profileId, className }) => {
  const { tokens, isLoading, reactionsToNextGivingToken } = useBoostTokens(profileId);

  if (isLoading || !tokens) return null;

  const progressPercent = ((10 - reactionsToNextGivingToken) / 10) * 100;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 cursor-help',
              className
            )}
          >
            <Zap className="h-4 w-4 text-amber-500 fill-amber-500" />
            <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
              {tokens.balance}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="w-64 p-3">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500 fill-amber-500" />
              <span className="font-semibold">Boost Tokens</span>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Use tokens to boost your posts for 24 hours of priority placement!
            </p>

            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Next token progress</span>
                <span className="text-foreground font-medium">
                  {10 - reactionsToNextGivingToken}/10 reactions given
                </span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>

            <div className="pt-2 border-t text-xs text-muted-foreground space-y-1">
              <p>• Give 10 reactions = earn 1 token</p>
              <p>• Receive 10 reactions = earn 1 token</p>
              <p>• Make Draft Board = earn 2 tokens</p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BoostTokenDisplay;
